import React, { ComponentProps, memo, ReactNode, useMemo } from 'react';
import { Button, Spinner, Icon } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import type { SaveStatus } from 'create/bulk/FVSS/hooks/useSavePrescriptions/types';

export enum JobStatus {
  PENDING = 'PENDING',
  EXECUTING = 'EXECUTING',
  SUCCEEDED = 'SUCCEEDED',
  CANCELLED = 'CANCELLED',
  INVALID = 'INVALID',
  FAILED = 'FAILED',
}

type LoadingItem = {
  id: number | string;
  label: string;
  status: JobStatus | SaveStatus;
  description?: string | ReactNode;
}

interface BulkLoadingProps {
  items: LoadingItem[];
  isLoading?: boolean;
  loadingText?: string;
  buttonProps?: ComponentProps<typeof Button>;
}

// TODO: This variable is an example of data to use the component. Remove this comment on component's usage.
// const data = [
//   {
//     id: 1,
//     label: 'Alpha South',
//     status: JobStatus.SUCCEEDED
//   },
//   {
//     id: 2,
//     label: 'Beta North',
//     status: JobStatus.SUCCEEDED
//   },
//   {
//     id: 3,
//     label: 'Delta East',
//     status: JobStatus.SUCCEEDED
//   },
//   {
//     id: 4,
//     label: 'Gamma West',
//     status: JobStatus.EXECUTING
//   },
//   {
//     id: 5,
//     label: 'HH A Really Long Field Name',
//     status: JobStatus.FAILED,
//     description: 'Insufficient Data. Field Removed.'
//   },
//   {
//     id: 6,
//     label: 'Jacob',
//     status: JobStatus.FAILED,
//     description: <p><a>Upgrade Plan</a> to use. Field Removed.</p>
//   },
//   {
//     id: 7,
//     label: 'Kellog',
//     status: JobStatus.PENDING,
//   },
//   {
//     id: 8,
//     label: 'O’Riely',
//     status: JobStatus.PENDING,
//   },
// ];

const statusData = {
  [JobStatus.PENDING]: {
    description: 'bulk-loading-status-pending',
    icon: <Icon glyph={Icon.set.CLOCK} className="pending-icon" />
  },
  [JobStatus.EXECUTING]: {
    description: 'bulk-loading-status-executing',
    icon: <Spinner size="24" />
  },
  [JobStatus.SUCCEEDED]: {
    description: 'bulk-loading-status-succedeed',
    icon: <Icon glyph={Icon.set.MATERIAL_CHECK} className="succeeded-icon" />
  },
  [JobStatus.CANCELLED]: {
    description: 'bulk-loading-status-failed',
    icon: <Icon glyph={Icon.set.WARNING_CIRCLE} className="failed-icon" />
  },
  [JobStatus.INVALID]: {
    description: 'bulk-loading-status-invalid',
    icon: <Icon glyph={Icon.set.WARNING_CIRCLE} className="failed-icon" />
  },
  [JobStatus.FAILED]: {
    description: 'bulk-loading-status-failed',
    icon: <Icon glyph={Icon.set.WARNING_CIRCLE} className="failed-icon" />
  },
};

const BulkLoadingItem = memo(function Item({ id, label, status, description }: LoadingItem) {
  const { description: statusDescription, icon: statusIcon } = statusData[status]
  || statusData[JobStatus.FAILED];

  return (
    <li key={id}>
      {statusIcon}
      <div>
        <p>{label}</p>
        <div
          data-test-id={statusDescription}
          className="loading-item-description"
        >
          {description ?? getMessage(statusDescription)}
        </div>
      </div>
    </li>
  );
});

const BulkLoading = ({ items, isLoading, loadingText, buttonProps }: BulkLoadingProps) => {
  const hasItems = useMemo(() => !!items?.length, [items]);

  return (
    <div
      className="bulk-loading-wrapper"
      data-test-id="bulk-loading-wrapper"
    >
      {isLoading && (
        <div className="loading-subheader">
          <Spinner size="40" />
          <p>{loadingText ?? getMessage('bulk-loading-subheader-text')}</p>
        </div>
      )}
      {hasItems && (
        <main>
          <ul>{items.map((item) => <BulkLoadingItem key={item.id} {...item} />)}</ul>
          {!!buttonProps && (
            <div
              className="loading-actions"
              data-test-id="bulk-loading-actions"
            >
              <Button
                {...buttonProps}
                mode="secondary"
                testId="bulk-loading-button-action"
              >
                {buttonProps.children ?? getMessage('bulk-loading-action-button-text')}
              </Button>
            </div>
          )}
        </main>
      )}
    </div>
  );
};

export default BulkLoading;
